/**
 * Class constants for the data table surface.
 *
 * Every interactive cell is a real button inside a `<th scope="col">`, because
 * a clickable `<div>` header is not reachable by keyboard and has no role.
 */

export const dataTableStyles = {
  root: 'flex flex-col gap-3',

  /**
   * The single card that wraps the tabs row, filter bar and table body.
   *
   * Tabs sit on top, the table sits at the bottom, and the borders are owned
   * by the surface so there is no visible gap between the controls and the
   * rows they act on. Internal sections use borders (not their own cards) so
   * the surface reads as one continuous panel.
   */
  surface: 'flex flex-col overflow-hidden rounded-surface border border-border bg-surface-raised',

  /**
   * The surface in CARD view: no card.
   *
   * In table view the surface is a card because the rows need a frame — a
   * table with no border bleeds into the canvas and the header tint has
   * nothing to sit on. In card view the cards ARE the objects: wrapping a grid
   * of bordered cards in a second bordered card draws a box around a box and
   * makes every card read as a cell of one big table rather than as its own
   * record. So the surface drops its border, radius and fill and becomes a
   * plain column, and the cards sit directly on the page canvas.
   *
   * `overflow-visible` rather than `overflow-hidden`: the sticky chrome inside
   * cannot stick if an ancestor clips it, and there is no rounded corner left
   * for the clip to protect.
   */
  surfaceBare: 'flex flex-col',

  /**
   * The sticky chrome in card view.
   *
   * The tabs row and the filter bar stay pinned to the top of the scroll
   * container while the cards scroll under them, so the saved views and the
   * search field are reachable without scrolling back up through a long grid.
   *
   * `z-30` puts the chrome above the cards. The cards are not positioned, so
   * they cannot out-stack it, but the value is stated rather than left to
   * source order because a future card that gains a positioned child would
   * otherwise scroll over the controls.
   *
   * THE HEADER IS THE SAME HEADER IN BOTH VIEWS.
   * Card view removes the BODY's card — the border, the radius and the white
   * fill around the rows — but the header above it is the same object in both
   * presentations: a tinted tabs band sitting on a white filter bar. So the
   * wrapper paints only the TOP BAND, in the tabs' own tint, and lets its two
   * children paint themselves exactly as they do inside the table-view
   * surface. Painting the whole wrapper white put a white strip above the
   * tinted tabs band, and the header read as three stacked bands
   * (white / grey / white) instead of one.
   *
   * THE HEADER KEEPS ITS FRAME.
   * The header is still a card in card view — it keeps the border and the
   * rounded TOP corners, so it reads as the head of the panel rather than as a
   * loose band floating on the canvas. Only the BODY below it is bare. The
   * bottom corners are square because the header is not the bottom of
   * anything: the cards continue below it, and a rounded bottom edge would
   * draw a curve the grid then has to sit under. The filter bar's own
   * `border-b` closes the frame at the bottom, so the header is a complete
   * box: border on three sides plus the filter bar's rule.
   *
   * THE TOP BAND IS OUTSIDE THE FRAME, NOT INSIDE IT.
   * `-top-4 pt-4` closes the gap the page gutter leaves above the chrome. The
   * scroll container pads its content by 1rem, and a sticky element pins to
   * the CONTENT box — so with `top-0` the cards scroll through that 1rem band
   * and are visible above the pinned controls. Pinning at `-top-4` instead
   * moves the pinned edge up into the padding, and `pt-4` pads the content
   * back down so the tabs and filter bar keep their position. The value
   * matches the shell's `pt-4`; if that gutter changes, this must change with
   * it.
   *
   * That band is painted with the page canvas tint (`surface-sunken`) and
   * carries no border, because it is not part of the header — it is the page
   * gutter the header has to cover while pinned. The frame lives on
   * `stickyChromeFrame` INSIDE this wrapper, so the border and the rounded
   * corners start at the tabs rather than 1rem above them. An earlier version
   * put `pt-4` and the frame on the same element, which drew the border around
   * the empty band and left the tabs looking inset from their own card's top
   * edge.
   *
   * The band MUST be filled, not transparent. A transparent band leaves a
   * 1rem slot between the top bar and the pinned header through which the
   * cards scroll in full view — the header appears to float with a gap above
   * it. Filling it with the canvas tint makes the band read as the canvas
   * continuing to the top bar, so the header card sits flush under the bar
   * with no visible seam. The tint matches `main`'s own background; if the
   * shell's canvas colour changes, this must change with it.
   *
   * `-mt-4` cancels the band's own height in the static flow. The band is only
   * needed while the chrome is PINNED — at rest it is 1rem of empty canvas
   * sitting between the page header and the tabs card, on top of the section
   * gap, which on a phone is a visible hole above the first control. Pulling
   * the wrapper up by the same 1rem removes it from the flow without touching
   * the pinned position: a sticky element's offset is measured from the scroll
   * container, not from its margin box, so the pin still lands at `-top-4` and
   * the band still covers the gutter once the chrome sticks.
   */
  stickyChrome: 'sticky -top-4 z-30 -mt-4 bg-surface-sunken pt-4',

  /**
   * The header's frame, inside the sticky wrapper.
   *
   * This is the element that is actually a card: the border, the rounded top
   * corners and the tinted fill. It sits inside `stickyChrome` so the wrapper
   * can carry the transparent top band the sticky pin needs without the frame
   * stretching up into it.
   *
   * The tint is `surface-sunken` — the tabs strip's own fill — so the band
   * above the tabs reads as the strip continuing to the top bar rather than as
   * a white gap. It is on this element rather than on the tabs row because the
   * tabs row is absent when a table has no saved views, and the band has to be
   * painted either way.
   */
  stickyChromeFrame: 'rounded-t-surface border-x border-t border-border bg-surface-sunken',

  /**
   * The first row: saved views as a tab strip.
   *
   * The strip is a TINTED BAND across the top of the card, and the tabs sit
   * inside it. The band is `bg-surface-sunken` — the same tint the page canvas
   * uses — so the strip reads as a recessed rail rather than as part of the
   * card's white surface.
   *
   * NO BOTTOM BORDER. The strip used to carry a `border-b` rule with the active
   * tab rising out of it. That rule is gone: the separation between the strip
   * and the content below is now carried by the TINT itself, which is a
   * quieter edge than a drawn line and does not have to be interrupted under
   * the active tab.
   *
   * NO LEFT PADDING. The first tab starts flush against the card's left edge,
   * so the strip reads as a band the tabs are laid into rather than as a
   * container with a margin around them. Only the right side keeps padding, so
   * the last tab does not run into the card's edge when the strip is scrolled
   * to its end.
   *
   * NO TOP PADDING either. The tabs start at the card's top edge, so the tinted
   * band is exactly as tall as the tabs and there is no empty strip above them.
   *
   * `items-stretch` rather than `items-end`: with the rule gone there is
   * nothing for the tabs to sit ON, so they fill the band's full height and the
   * active tab's white reaches the strip's top and bottom edges.
   *
   * `flex-row` is set explicitly to override the base-ui Tabs primitive's
   * `flex-col` direction; without it the tabs end up centred in a column
   * instead of left-aligned in a row.
   *
   * `rounded-t-surface` matches the frame the strip sits in. In table view the
   * surface clips it with `overflow-hidden`, so the value is inert there; in
   * card view the sticky chrome is the frame and does NOT clip (it cannot, or
   * the sticky child would not stick), so the strip has to round its own top
   * corners or its tint squares off the header's curve.
   *
   * ON MOBILE THE STRIP FADES AT ITS RIGHT EDGE. The tabs are wider than a
   * phone, so the strip scrolls — but a tab sliced off mid-word ("Suspe…")
   * reads as a layout bug, not as an invitation to scroll. The mask fades the
   * last 2rem of the strip into the band's own tint, which is the native
   * affordance for "there is more here": the content dissolves rather than
   * being cut. It is applied below `sm` only, because from `sm` up the strip
   * fits and a fade over a fully-visible last tab would just dim it.
   */
  tabsRow:
    'no-scrollbar flex flex-row items-stretch gap-0 overflow-x-auto rounded-t-surface bg-surface-sunken pr-3 [mask-image:linear-gradient(to_right,#000_calc(100%-2rem),transparent)] sm:pr-4 sm:[mask-image:none]',

  /**
   * The tab list itself.
   *
   * `gap-0` JOINS the tabs into one continuous bar, and the separators between
   * them are drawn by each tab's own right border rather than by a gap. A gap
   * would make each tab its own object — the segmented-control language — and
   * the reference is the opposite: the tabs share edges so the strip reads as
   * one bar with one selected segment.
   *
   * `border-b-0` because the strip has no bottom rule any more; a border here
   * would draw one back in.
   */
  tabsRowList: 'relative gap-0 border-b-0',

  /**
   * Each tab.
   *
   * THESE ARE TABS, NOT BUTTONS, AND THE STYLES HAVE TO SAY SO.
   * The base-ui primitive renders a `<button>`, and the shared `tabsStyles`
   * trigger's look had leaked in: `rounded-control` gave every tab rounded
   * corners and `justify-center` centred the label in a fixed-width box. Both
   * are button language — a button is a discrete object with its own outline,
   * a tab is a segment of a bar. They are overridden here with `rounded-none`
   * and `justify-start`, so a tab is a plain rectangular segment whose label
   * sits where the text starts and whose edges are square.
   *
   * The overrides have to be spelled out rather than merely omitted: the
   * shared trigger class is applied BEFORE this one, so leaving the properties
   * out would let the shared values win.
   *
   * THE SEPARATOR IS A RIGHT BORDER ONLY.
   * `border-r border-border` draws one rule down the right side of each tab, so
   * each tab is divided from the one after it by a single shared edge. The last
   * tab's border is suppressed with `last:border-r-0`, because a rule at the
   * very end of the strip would read as the strip's own outline rather than as
   * a divider between two tabs.
   *
   * INACTIVE TABS ARE FLAT LABELS on the tinted band: no fill of their own, so
   * the strip's gray shows through and they read as unselected.
   *
   * THE ACTIVE TAB IS WHITE WITH PRIMARY-COLOURED TEXT.
   * `bg-surface-raised` lifts it off the tinted band, and `text-accent` — the
   * brand purple — marks it as the current view. The colour is doing the work
   * here rather than a border or a shadow, which is why the active tab keeps
   * the same separator as its neighbours: the fill and the text colour are
   * enough to single it out.
   *
   * The active state is driven by `data-active`, which is what the base-ui Tabs
   * primitive emits; `data-selected` would never fire.
   */
  tabsRowTrigger:
    'relative inline-flex items-center justify-start gap-1.5 whitespace-nowrap rounded-none border-r border-border px-2.5 py-1.5 text-sm font-medium text-content-muted transition-colors last:border-r-0 hover:text-content data-[active]:bg-surface-raised data-[active]:text-accent sm:gap-2 sm:px-3',

  /**
   * The count next to a tab label. A small rounded pill so a number reads as
   * a count and not as decoration.
   *
   * THE FILL IS `bg-border`, NOT `bg-surface-sunken`.
   * The badge used to reuse the sunken surface tint — which is the SAME token
   * the strip itself is painted with. On an inactive tab the badge therefore
   * had no fill at all: it was a gray pill on a gray band, and the count
   * disappeared. `bg-border` is a full step darker than the strip, so the pill
   * reads as a pill on both states.
   *
   * The active tab keeps the same fill. It sits on white rather than on the
   * tint, so the contrast is even stronger there, and the text colour is what
   * carries the emphasis — the badge does not need a second treatment.
   */
  tabsRowCount:
    'ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-border px-1.5 py-px text-2xs font-medium tabular-nums text-content-muted',

  /**
   * The active tab's badge. Only the TEXT changes: the fill stays `bg-border`
   * so the pill keeps the same weight on both states, and the count darkens to
   * full-strength content so it gains emphasis with the tab rather than
   * competing with it.
   *
   * It used to reset the fill to `bg-surface-sunken`, which on the active tab
   * meant a LIGHTER pill than the inactive ones — the emphasis ran backwards.
   */
  tabsRowCountActive: 'text-content font-semibold',

  /**
   * The second row: search on the left, every other control (column filters,
   * result count, Columns disclosure, view toggle) on the right.
   *
   * Stacks vertically on mobile (below the `sm` breakpoint) and runs as a
   * single row on desktop. The trailing group stops pushing to the right with
   * `ml-auto` once the controls wrap, because a wrapped trailing group ends
   * up narrower than the controls and the auto-margin starts to fight the
   * flexbox rather than align anything.
   *
   * ON MOBILE THE SEARCH AND THE FILTERS SHARE ONE ROW.
   * Stacking them put the search on one line and every filter on the next,
   * which on a phone is two rows of controls where one will do — the search
   * field is the only thing that needs the full width, and it can take what
   * the filters leave. `flex-row` with the search as `flex-1` and the trailing
   * group as `shrink-0` gives the search the leftover width and keeps the
   * filters at their natural size beside it. The padding drops to `py-2` to
   * match the desktop bar, since the row is now the same height either way.
   */
  filterBar:
    'flex flex-row flex-wrap items-center gap-2 border-b border-border bg-surface-raised px-3 py-2 sm:flex-nowrap sm:gap-3',

  /**
   * The trailing group holds column-bound filters, the result count, the
   * Columns disclosure and the view toggle. On mobile it sits beside the
   * search field and wraps below it only when the filters do not fit; on
   * desktop it right-aligns with `ml-auto`.
   */
  filterBarTrailing: 'flex shrink-0 flex-wrap items-center gap-2 sm:ml-auto',

  filterBarSearch: 'relative min-w-0 flex-1 sm:max-w-xs',

  /**
   * A magnifying-glass icon sits over the input on the left. It is decorative
   * (`aria-hidden`) — the visible `<label>` carries the accessible name — and
   * pointer-events-none keeps clicks falling through to the field.
   */
  searchIcon:
    'pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle',

  search:
    'w-full rounded-control border border-border-strong bg-surface py-1.5 pl-8 pr-2 text-sm text-content outline-none placeholder:text-content-subtle focus-visible:border-accent',

  /** A column-bound filter: label + control, side by side. */
  columnFilter: 'flex min-w-0 items-center gap-1.5 text-sm',

  /**
   * The filter's label.
   *
   * Hidden below `sm`: on a phone the filter row has to fit the search field
   * and the control on one line, and the label is the part that can go — the
   * select's own placeholder ("Any") and its options name the field well
   * enough, and the label is still in the DOM for a screen reader.
   */
  columnFilterLabel: 'hidden shrink-0 text-content-muted sm:inline',

  /**
   * The same label, kept visible at every width.
   *
   * Used when the filter row carries no search field: the label was hidden
   * below `sm` to make room for the search input, and with no search input
   * there is nothing to make room for — two unlabelled "Any" dropdowns name
   * nothing at all.
   */
  columnFilterLabelAlways: 'shrink-0 text-content-muted',

  columnFilterInput:
    'w-full min-w-0 flex-1 rounded-control border border-border-strong bg-surface px-2 py-1.5 text-sm text-content outline-none placeholder:text-content-subtle focus-visible:border-accent sm:w-40 sm:flex-none',

  /**
   * The select is `min-w-32` from `sm` up so a row of filters lines up, but on
   * mobile that 128px floor left the search field too narrow to read its own
   * placeholder. Below `sm` it shrinks to its content instead.
   */
  columnFilterSelect: 'min-w-0 sm:min-w-32',

  /**
   * A button-styled `<details>` disclosure. Inline so it sits flush with the
   * view toggle; the trigger is bordered but not filled, which keeps it from
   * looking like a primary action.
   */
  disclosure:
    'relative inline-flex items-center gap-1 rounded-control border border-border-strong bg-surface-raised px-2.5 py-1.5 text-sm font-medium text-content transition-colors hover:bg-surface-sunken',

  /** A more compact disclosure used inline in the filter bar. */
  disclosureInline: 'hidden px-2.5 text-xs sm:inline-flex',

  disclosurePanel:
    'absolute right-0 z-30 mt-1 min-w-48 rounded-surface border border-border bg-surface-raised p-2 shadow-lg',

  disclosureOption:
    'flex items-center gap-2 rounded-control px-2 py-1.5 text-sm text-content hover:bg-surface-sunken',

  resultCount: 'text-sm text-content-muted',

  /** The presentation toggle: a pair of buttons inside a single rounded group. */
  viewToggleGroup:
    'hidden items-center overflow-hidden rounded-control border border-border-strong bg-surface-raised sm:inline-flex',

  /**
   * One button in the presentation toggle.
   *
   * `h-7 w-7` rather than `h-9 w-9`: the toggle sits in the filter bar beside
   * the search field and the column filters, and at 36px it was the tallest
   * thing in the row — it set the bar's height on its own. At 28px it matches
   * the other compact controls and the bar reads as one line of controls
   * rather than as a row with one oversized item in it.
   *
   * The icon is sized by the caller (`size={14}`), so the smaller box does not
   * shrink the glyph — it only removes the empty padding around it.
   */
  viewToggle:
    'inline-flex h-7 w-7 items-center justify-center text-content-muted transition-colors hover:bg-surface-sunken disabled:cursor-default disabled:bg-surface-sunken disabled:text-accent',

  viewToggleActive: 'text-accent',

  viewport:
    'overflow-auto bg-surface-raised focus-visible:outline-2 focus-visible:outline-accent',

  table: 'w-full border-collapse text-left text-sm',

  /** An optional in-table caption. Visually hidden; read as the table's name. */
  tableCaption: 'sr-only',

  /**
   * Header cells use the same horizontal padding as body cells so the column
   * edges line up. The background is a barely-there tint that reads as a row
   * separator, not a section header.
   *
   * The header is PINNED to the top of the scrolling viewport, so the column
   * names stay readable while the rows scroll under them. `z-20` puts it above
   * the pinned body cells (`z-10`) — a pinned column that scrolled over the
   * header would hide the very label that explains it.
   */
  headerCell: 'sticky top-0 z-20 border-b border-border bg-surface-sunken px-3 py-2 text-content',

  headerCellSortable: 'p-0',

  sortButton:
    'flex w-full items-center gap-1 px-3 py-2 text-left font-semibold text-content transition-colors hover:bg-surface hover:text-accent',

  /**
   * The chevron is muted by default (it shows that the column IS sortable,
   * not that it is currently sorted) and turns accent once the column holds
   * the active sort. Muted at rest keeps the row of headers from looking
   * like a column of buttons.
   */
  sortIcon: 'shrink-0 text-content-muted transition-colors data-[active]:text-accent',

  /**
   * Cells are deliberately DENSE. A directory table is read by scanning a
   * column, not by reading a row, so the tighter the rows the more of the
   * column fits on screen at once. The padding is enough to keep the
   * letterforms off the cell edges and no more.
   */
  cell: 'border-b border-border px-3 py-2 text-content align-middle',

  cellNumeric: 'border-b border-border px-3 py-2 text-right text-content align-middle tabular-nums',

  checkboxCell: 'w-12 border-b border-border px-3 py-2 align-middle',

  /**
   * A row sits on the surface and tints on hover, then tints harder on
   * selection. The two states are tone-of-background, not colour, so a row
   * that is being read (not hovered) is still legible.
   *
   * `group/row` names the row so a pinned cell inside it can follow the row's
   * hover and selected tints. Without the group the pinned cell would keep its
   * own resting fill while the rest of the row darkened, leaving a white
   * stripe down the pinned column.
   */
  row: 'group/row transition-colors hover:bg-surface-sunken/70 data-[selected]:bg-accent/10',

  rowButton: 'focus-visible:outline-2 focus-visible:outline-accent',

  skeletonRow: 'animate-pulse border-b border-border',

  skeletonCell: 'h-3 rounded-control bg-surface-sunken',

  stateCell: 'px-3 py-10 text-center',

  stateTitle: 'text-sm font-semibold text-content',

  stateDescription: 'mt-1 text-sm text-content-muted',

  alert:
    'border-b border-critical/30 bg-critical/10 px-3 py-2 text-sm font-medium text-critical',

  bulkBar:
    'flex flex-wrap items-center gap-2 border-b border-accent/30 bg-accent/10 px-3 py-2',

  bulkCount: 'text-sm font-medium text-content',

  /**
   * The footer: numeric pagination on the left, per-page selector and range
   * text on the right. The two sides answer two different questions: where
   * can I go? (pagination) and where am I? (range / per-page).
   *
   * Renders INSIDE the surface card (a top border separates it from the
   * table body). It no longer carries its own border or rounded corners —
   * the surface owns those, and the footer reads as the bottom row of one
   * continuous panel rather than a separate card.
   */
  footer:
    'flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-raised px-3 py-2 sm:flex-nowrap',

  footerTrailing: 'flex flex-wrap items-center gap-3 whitespace-nowrap sm:ml-auto sm:flex-nowrap',

  pagination: 'flex items-center gap-0.5 whitespace-nowrap',

  rangeText: 'whitespace-nowrap text-sm text-content-muted',

  pageButton:
    'rounded-control border border-border-strong bg-surface-raised px-2.5 py-1.5 text-sm font-medium text-content transition-colors hover:bg-surface-sunken disabled:pointer-events-none disabled:opacity-50',

  pageSizeGroup: 'flex items-center gap-2 whitespace-nowrap',

  /**
   * The rows-per-page dropdown.
   *
   * Narrower and shorter than the default control. The only values it ever
   * holds are two-digit page sizes, so `min-w-14` reserved room for a label
   * that cannot occur and left the control looking padded out beside the range
   * text.
   *
   * `h-7` overrides the `Select`'s own `size="sm"` height. The shared control
   * only offers `sm` (32px) and `md`, and neither is right here: the footer is
   * a summary line, and a 32px control beside 28px page buttons made the
   * dropdown the tallest thing in the row. The height is set through the
   * `className` prop, which the trigger appends AFTER its own size classes, so
   * this wins without touching the shared component.
   */
  pageSizeSelect: 'h-7 min-w-12',

  /**
   * A numeric page button, and the chevrons at either end.
   *
   * `h-7 min-w-7` rather than `h-8 min-w-8`: the footer is a summary line, not
   * a control surface, and eight 32px squares across the bottom of the card
   * made the pagination the heaviest thing in it. At 28px the row still clears
   * the 24px minimum touch target when the buttons are spaced, and the strip
   * stops competing with the table above it.
   *
   * `px-1.5` and `text-2xs` follow from the same reasoning: the buttons hold at
   * most two digits, so the horizontal padding only has to keep the number off
   * the border, and the smaller type keeps the digits from filling the box.
   */
  pageButtonCompact:
    'inline-flex h-7 min-w-7 items-center justify-center rounded-control border border-border-strong bg-surface px-1.5 text-2xs font-medium text-content transition-colors hover:bg-surface-sunken disabled:pointer-events-none disabled:opacity-50',

  pageButtonCurrent: 'border-accent bg-accent/10 text-accent',

  ellipsis: 'px-1 text-sm text-content-muted',

  grid: 'grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3',

  /**
   * The grid in card view sits on the page canvas, not inside a card.
   *
   * NO HORIZONTAL GUTTER AT ANY WIDTH.
   * The shell's `<main>` already pads the page by `px-4`, so a horizontal
   * gutter here added a SECOND inset on each side and the cards sat 28px in
   * from the screen edge — a fifth of a 320px viewport spent on margin, and on
   * a desktop a band of empty canvas down both sides of the grid. The cards
   * line up with the header and the tabs strip above them, which are already
   * inset by the shell alone. Only the vertical padding remains, so the first
   * row clears the chrome and the last row clears the footer.
   */
  gridBare: 'grid grid-cols-1 gap-3 py-3 sm:grid-cols-2 lg:grid-cols-3',

  /*
   * A card is a COLUMN, not a stack of paragraphs.
   *
   * The grid stretches every card in a row to the tallest one, so a card with
   * a two-line title and a card with a one-line title are the same height. The
   * card is a flex column so its content stacks predictably inside that height
   * rather than collapsing into a run of inline text.
   *
   * THE CARD IS WHITE, AND ITS HOVER DOES NOT BLEND INTO THE CANVAS.
   * The card used to be `bg-surface` (99% white) and hover to `bg-surface-sunken`
   * — the page canvas itself. So a hovered card became the colour of the page
   * behind it and its edges dissolved: the border was the only thing left
   * saying where the card ended, and on a grid of cards the hovered one read as
   * a hole rather than as a raised surface. The card is now `bg-surface-raised`
   * (pure white in light, and in dark a step LIGHTER than the sunken canvas, so
   * it still reads as raised) and the hover keeps that fill, strengthening the
   * border instead. The card stays a card; only its edge sharpens.
   */
  card:
    'relative flex flex-col rounded-surface border border-border bg-surface-raised p-3 transition-colors hover:border-border-strong',

  cardHeader: 'mb-2 text-sm font-semibold text-content',

  /*
   * The body rows are a definition list in all but markup: a muted label and
   * its value on one line. `gap-1` between rows rather than a margin on each
   * keeps the spacing even whether a row is one line or two.
   *
   * `mb-3` is the gap between the last body row and the card's bottom edge.
   * The card carries no footer any more — the actions live in the sheet — so
   * this is simply the card's own bottom padding, kept on the body so the
   * spacing is the same whether the card has one field or six.
   */
  cardBody: 'mb-3 flex flex-col gap-1 text-sm text-content-muted',

  cardRow: 'flex items-baseline gap-1.5',

  cardRowLabel: 'shrink-0 font-medium',

  /*
   * The card's tap target, at EVERY width.
   *
   * A single button stretched over the whole card, sitting UNDER the content.
   * It is a real `<button>` rather than a click handler on the `<li>` so it is
   * focusable, announced as a button, and activated by Enter and Space for
   * free — a clickable `<li>` is invisible to a keyboard and to a screen
   * reader.
   *
   * It is an overlay rather than a wrapper because a cell may render its own
   * control (the requirements card's Execution cell is a link). A wrapping
   * button would put that link inside a button, which is invalid HTML and
   * leaves the browser unable to say which control a tap meant. As an overlay
   * the cell's own control is raised above it (`relative z-10`) and wins the
   * tap; everything else falls through to the sheet.
   *
   * `inset-0` covers the card's padding box, so the whole card is tappable
   * including its padding. `z-0` keeps it under the content; the content is
   * not positioned, so it paints above a `z-0` sibling by source order.
   */
  cardOverlay: 'absolute inset-0 z-0 cursor-pointer rounded-surface',

  /*
   * The card's content, lifted above the overlay button so the text is not
   * covered by it.
   *
   * `pointer-events-none` lets a tap on the text fall THROUGH to the overlay
   * underneath, so the whole card still opens the sheet. A cell that renders
   * its own control re-enables pointer events (`cardInteractive`), so it wins
   * the tap instead.
   */
  cardContent: 'pointer-events-none relative z-10 flex flex-col',

  /*
   * A cell that renders its own control. It re-enables pointer events (the
   * content above the overlay is `pointer-events-none`) and is raised above
   * the overlay, so it receives the tap instead of the sheet opening.
   */
  cardInteractive: 'pointer-events-auto relative z-10',

  /*
   * The sheet a card opens.
   *
   * It shows the SAME fields the card shows — the sheet is not a second,
   * richer view, it is the card's own content given room to breathe plus the
   * actions the card no longer carries. The heading is the sheet's title so
   * the panel is announced by the record it describes.
   *
   * On a phone it is anchored to the bottom edge; on a pointer it is anchored
   * to the right, because a bottom sheet on a wide screen is a strip of content
   * pinned to the bottom of a tall window with the record it describes far
   * above it. The classes below are shared by both edges.
   */
  cardSheetHeader:
    'flex flex-row items-start justify-between gap-3 border-b border-border p-4',
  cardSheetTitle: 'min-w-0 flex-1 text-lg font-semibold text-content',
  cardSheetClose:
    'inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-control border border-border bg-transparent p-0 text-content-muted transition-colors hover:border-border-strong hover:bg-surface-sunken hover:text-content',
  cardSheetBody: 'flex flex-1 flex-col gap-2 p-4',
  cardSheetRow: 'flex items-baseline gap-1.5 text-sm',
  cardSheetRowLabel: 'shrink-0 font-medium text-content-muted',
  cardSheetFooter: 'flex flex-col gap-2 border-t border-border p-4',

  sentinel: 'h-px w-full',

  /**
   * A pinned column.
   *
   * The background is NOT optional. A sticky cell scrolls over the cells
   * beneath it, so without an opaque fill the columns it passes over show
   * through and the pinned column reads as a smear rather than as a column.
   *
   * The fill has to MATCH THE ROW IT SITS IN, which is why there are two
   * tokens rather than one:
   *
   * - `stickyCellRight` / `stickyCellLeft` are for BODY cells and carry
   *   `bg-surface-raised` — the row's own resting fill. They also carry the
   *   row's hover and selected tints, so a pinned cell tints with its row
   *   instead of staying white while the rest of the row darkens.
   * - `stickyHeaderCellRight` / `stickyHeaderCellLeft` are for HEADER cells
   *   and carry `bg-surface-sunken` — the header row's tint. A pinned header
   *   cell painted with the body fill would sit as a white notch in an
   *   otherwise tinted header row.
   *
   * `shadow-sm` draws the edge that separates the pinned column from the
   * content scrolling under it. It is a shadow rather than a border because a
   * border would be drawn on both sides of the column and read as a box.
   */
  stickyCellRight:
    'sticky right-0 z-10 bg-surface-raised shadow-sm group-hover/row:bg-surface-sunken group-data-[selected]/row:bg-accent/10',

  stickyCellLeft:
    'sticky left-0 z-10 bg-surface-raised shadow-sm group-hover/row:bg-surface-sunken group-data-[selected]/row:bg-accent/10',

  stickyHeaderCellRight: 'sticky right-0 top-0 z-30 bg-surface-sunken shadow-sm',

  stickyHeaderCellLeft: 'sticky left-0 top-0 z-30 bg-surface-sunken shadow-sm',

  stickySpacer: 'w-3',

  /**
   * An icon-only action in a table row.
   *
   * A fixed square, so the action column is narrow and the row height is set
   * by the data rather than by the controls. `rounded-control` keeps it on the
   * same corner radius as every other control in the app.
   *
   * The label lives in a tooltip and in `aria-label`; the button itself carries
   * no text.
   */
  rowAction:
    'inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-control border transition-colors',

  /**
   * A row action that cannot fire.
   *
   * `cursor-not-allowed` and a dimmed glyph, but NOT `pointer-events-none`: the
   * control stays hoverable so its tooltip can state why it is unavailable. A
   * truly inert square would leave the user with no way to learn the reason.
   */
  rowActionDisabled: 'cursor-not-allowed opacity-50',

  /**
   * The tones a row action can take.
   *
   * EVERY TONE IS OUTLINED. None of them is filled.
   *
   * A filled button in every row of a table is a wall of colour: the fill stops
   * meaning "this one is the action" and starts meaning "this table is a
   * control surface". An outline carries the same colour at a fraction of the
   * weight, so a row of actions reads as a row of affordances rather than as a
   * row of buttons competing with the data beside them.
   *
   * The colour still does the work of separating the tones: `primary` is the
   * accent purple, `danger` the critical red, `warning` the caution amber, and
   * `neutral` an uncoloured border. The fill only appears on hover, where it
   * confirms the pointer is on the control.
   */
  rowActionTone: {
    primary: 'border-accent bg-transparent text-accent hover:bg-accent-soft',
    neutral:
      'border-border bg-transparent text-content-muted hover:border-border-strong hover:bg-surface-sunken hover:text-content',
    danger: 'border-critical bg-transparent text-critical hover:bg-critical-soft',
    warning: 'border-caution bg-transparent text-caution hover:bg-caution-soft',
  },

  paginationCompact:
    'flex flex-wrap items-center gap-1 px-2 py-1 text-xs text-content-muted',
} as const;
